import {useJobContext, useNotificationContext} from 'contexts';
import {useCallback} from 'react';
import {useNavigate} from 'react-router-dom';
import {PartialJob} from 'shared/types/PartialJob';
import {useMenuContext} from 'components/customer/RoomPlanner/components/Menu/MenuContext';
import {
    DialogOverrides,
    useUnsavedChangesDialog,
} from 'shared/components/ConfirmationDialog/UnsavedChangesDialog';

export interface CancelButtonProps {
    label?: string;
    to?: string;
    noDialog?: boolean;
    noRedirect?: boolean;
    iconName?: string;
    dialogOptions?: Partial<DialogOverrides>;
    beforeRedirect?: () => boolean | Promise<boolean>;
    onDiscard?: () => void | Promise<void>;
    onSave?: () => void | boolean | Promise<void | boolean>;
}

export const useCancelButton = ({
    to = '',
    noDialog = false,
    noRedirect = false,
    dialogOptions = {},
    beforeRedirect,
    onDiscard,
    onSave,
}: CancelButtonProps) => {
    const {job, room} = useJobContext() as PartialJob;
    const {addMessages} = useNotificationContext();
    const navigate = useNavigate();
    const {dialog, showDialog} = useUnsavedChangesDialog();
    const {closeProductEditorMenu} = useMenuContext();

    const handleRedirect = useCallback(async () => {
        const {displayId: jobId} = job;
        const {id: roomId} = room;

        // clear errors before redirecting
        if (addMessages) {
            addMessages({
                type: 'error',
                messages: [],
            });
        }

        if (beforeRedirect && typeof beforeRedirect == 'function') {
            const goforward = await beforeRedirect();

            if (!goforward) {
                return;
            }
        }

        if (to) {
            let url = to;
            if (jobId) url = url.replace(':jobId', String(jobId));

            if (roomId) url = url.replace(':roomId', String(roomId));

            void navigate(url !== to ? url : to);
        } else {
            if (noRedirect) {
                closeProductEditorMenu && closeProductEditorMenu();
            } else {
                // Default to the dashboard if no to link is provided
                if (jobId && roomId) {
                    to = `/v2/job/${jobId}/room/${roomId}/dashboard`;
                } else if (jobId && typeof roomId === 'undefined') {
                    to = `/v2/job/${jobId}/dashboard`;
                } else {
                    to = `/v2`;
                }

                void navigate(to);
            }
        }
    }, [
        to,
        job,
        room,
        noRedirect,
        beforeRedirect,
        closeProductEditorMenu,
        addMessages,
        navigate,
    ]);

    const handleDiscardAndRedirect = useCallback(async () => {
        if (onDiscard) {
            await onDiscard();
        }
        await handleRedirect();
    }, [onDiscard, handleRedirect]);

    const handleSaveAndRedirect = useCallback(async () => {
        if (onSave) {
            const saved = await onSave();
            if (saved === false) {
                return;
            }
        }
        await handleRedirect();
    }, [onSave, handleRedirect]);

    const handleCancelClick = useCallback(() => {
        if (noDialog) {
            void handleRedirect();
            return;
        }

        showDialog({
            onConfirmDiscard: handleDiscardAndRedirect,
            onConfirmSave: onSave ? handleSaveAndRedirect : undefined,
            dialogOverrides: dialogOptions,
        });
    }, [
        noDialog,
        showDialog,
        handleRedirect,
        onSave,
        handleDiscardAndRedirect,
        handleSaveAndRedirect,
        dialogOptions,
    ]);

    return {
        handleCancelClick,
        handleRedirect,
        handleDiscardAndRedirect,
        handleSaveAndRedirect,
        dialog,
    };
};
